import { useEffect, useState } from 'react';
import { supportApi } from '../api/Supportapi';

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CATEGORY_OPTIONS = {
  job_seeker: [
    'Account Issue',
    'Job Application',
    'Profile Issue',
    'Employer Issue',
    'Payment Issue',
    'Technical Issue',
    'Other',
  ],
  employer: [
    'Job Posting Issue',
    'Candidate Issue',
    'Employer Verification',
    'Account Issue',
    'Payment Issue',
    'Technical Issue',
    'Other',
  ],
};

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10MB

// role: 'job_seeker' | 'employer' — determines which category list is shown
export default function ContactSupportModal({ open, onClose, role = 'job_seeker', onCreated }) {
  const categories = CATEGORY_OPTIONS[role] || CATEGORY_OPTIONS.job_seeker;

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');

  const [category, setCategory] = useState(categories[0]);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [ticketNumber, setTicketNumber] = useState(null);

  useEffect(() => {
  if (!open) return;
  setCategory(categories[0]);
  const stored = localStorage.getItem('user');
  setProfile(stored ? JSON.parse(stored) : null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [open, role]);

  if (!open) return null;

  const reset = () => {
    setCategory(categories[0]);
    setSubject('');
    setDescription('');
    setAttachment(null);
    setError('');
    setTicketNumber(null);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.size > MAX_ATTACHMENT_BYTES) {
      setError('Attachment must be under 10MB.');
      e.target.value = '';
      return;
    }
    setError('');
    setAttachment(file || null);
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!subject.trim() || description.trim().length < 10) {
    setError('Please add a subject and a description of at least 10 characters.');
    return;
  }
  setSubmitting(true);
  setError('');
  try {
    const { ticket } = await supportApi.createTicket({
      category,
      subject: subject.trim(),
      description: description.trim(),
      attachments: attachment ? [attachment] : [],
    });
    setTicketNumber(ticket.ticket_number);
    onCreated?.(ticket);
  } catch (err) {
    setError(err.response?.data?.message || err.message || 'Something went wrong. Please try again.');
  } finally {
    setSubmitting(false);
  }
};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto border border-purple-100 border border-purple-100">
        <button onClick={handleClose} className="absolute top-4 right-4 text-gray-400 hover:text-purple-600 transition">
          <CloseIcon />
        </button>

        {ticketNumber ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 text-white shadow-md flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
              ✓
            </div>
            <h3 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent mb-1">Ticket submitted</h3>
            <p className="text-sm text-gray-500 mb-1">
              Your reference number is
            </p>
            <p className="text-sm font-bold text-purple-600 bg-purple-50 inline-block px-3 py-1 rounded-full mb-5">{ticketNumber}</p>
            <button
              onClick={handleClose}
              className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white text-sm font-semibold px-6 py-2.5 rounded-full shadow-sm transition"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h3 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent mb-5">Contact Support</h3>

            {profileError && (
              <p className="text-xs text-rose-600 mb-3">Couldn't load your profile details: {profileError}</p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Name</label>
                <input
                  type="text"
                  value={profileLoading ? 'Loading...' : (profile?.name || '')}
                  disabled
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-500 bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Email</label>
                <input
                  type="text"
                  value={profileLoading ? 'Loading...' : (profile?.email || '')}
                  disabled
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-500 bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Mobile</label>
                <input
                  type="text"
                  value={profileLoading ? 'Loading...' : (profile?.mobile || '')}
                  disabled
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-500 bg-gray-50"
                />
              </div>
            </div>

            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full mb-4 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#0D1B3D] bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            >
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Briefly describe your issue"
              className="w-full mb-4 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#0D1B3D] bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />

            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Message</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Give us as much detail as you can..."
              className="w-full mb-4 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#0D1B3D] bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent resize-none"
            />

            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Attachment (optional)</label>
            <input
              type="file"
              onChange={handleFileChange}
              className="w-full mb-2 text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-purple-50 file:text-purple-600 hover:file:bg-purple-100"
            />
            {attachment && <p className="text-xs text-gray-400 mb-2">{attachment.name}</p>}

            {error && <div className="bg-red-50 text-red-600 text-xs rounded-xl p-3 mb-3 border border-red-100">{error}</div>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full mt-2 bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 disabled:opacity-50 text-white text-sm font-semibold px-5 py-3 rounded-full shadow-sm transition"
            >
              {submitting ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}